import { sortByField } from "@lyfegen-platform/utils";
import { useTranslation } from "react-i18next";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { AgreementScope, Indication } from "../models/generated/graphql";
import { distinctBy } from "../utils/distinct-by";
import { useScopeFields } from "./useScopeFields";
import { useScopes } from "./useScopes";

const getIndicationsFromScopes = (scopes: Array<AgreementScope>): Array<Indication> => {
	return distinctBy(
		scopes.flatMap((scope) => scope.indications),
		(disease) => disease.code
	);
};

export const useAvailableIndications = (fieldController: FlexFieldController) => {
	const { indication } = useScopeFields(fieldController.parent.context);
	const { selectedScopes, allScopes } = useScopes(fieldController.parent.context);
	const { t } = useTranslation();

	if (!allScopes.length && indication) {
		return { options: [indication], isDiseaseConditionExcluded: false };
	}

	const selectedScopeIndications = getIndicationsFromScopes(selectedScopes);
	const otherIndications = getIndicationsFromScopes(allScopes)
		.filter((indication) =>
			selectedScopeIndications.every((selectedScopeIndication) => selectedScopeIndication.code !== indication.code)
		)
		.sort(sortByField("code"));

	const isIndicationConditionExcluded = selectedScopes.some((scope) =>
		scope.enrollingConditionSets.some((conditionSet) => conditionSet.isIndicationNotEnrollingCondition)
	);
	const isFreeText = selectedScopes.every((scope) => scope.indications.length === 0);

	return {
		options:
			isIndicationConditionExcluded && otherIndications.length
				? [
						...selectedScopeIndications.map((indication) => ({ ...indication, group: t("filtered-options") })),
						...otherIndications.map((indication) => ({
							...indication,
							group: t("organization-available-options"),
						})),
				  ]
				: selectedScopeIndications,
		isIndicationConditionExcluded,
		isFreeText,
	};
};
